import { useState, useEffect, useRef, useCallback } from 'react';
import { ethers } from 'ethers';
import { enforceBaseNetwork, getReadProvider } from '../utils/nftDiscovery';

// Global styles to ensure scrolling works
const globalStyles = `
  html, body {
    overflow-x: hidden;
    overflow-y: auto;
    height: auto;
    min-height: 100vh;
  }
  #root {
    overflow-x: hidden;
    overflow-y: auto;
    height: auto;
    min-height: 100vh;
  }
`;

// ─── Contract config ──────────────────────────────────────────────────────────
// Updated to use HootlandAgent contract address
const MINT_CONTRACT_ADDRESS = '0x4E8C7160ac34fc93a4D16DDa1817A27455809bA2';

const MINT_ABI = [
  'function mint(uint256 quantity) payable',
  'function currentPrice() view returns (uint256)',
  'function mintOpen() view returns (bool)',
  'function nextAgentId() view returns (uint256)',
  'function genesisSupply() view returns (uint256)',
  'function MAX_GENESIS() view returns (uint256)',
  'function MAX_PER_TX() view returns (uint256)',
  'function agents(uint256) view returns (uint256 agentId, uint256 agentType, uint256 tier, uint256 multiplier, address originalCollection, uint256 originalTokenId, uint256 x, uint256 y, uint256 lastClaimTime, uint256 totalEarned, bool active)',
  'function ownerOf(uint256 tokenId) view returns (address)',
  'function balanceOf(address owner) view returns (uint256)',
  'function totalSupply() view returns (uint256)',
  'event GenesisMinted(uint256 indexed agentId, address indexed owner, uint256 tier, uint256 multiplier, uint256 x, uint256 y)',
  'event Transfer(address indexed from, address indexed to, uint256 indexed tokenId)',
];

// ─── Tier config ──────────────────────────────────────────────────────────────
const TIERS = {
  1: { name: 'Agent',    icon: '🤖', color: '#dc2626', glow: 'rgba(220,38,38,0.25)',  bg: 'rgba(220,38,38,0.06)',  multi: '1.2x', rate: '12 OWL/hr',  prob: '50%', desc: 'Entry-level operative. Earns steadily, deploys fast.' },
  2: { name: 'Sentinel', icon: '⚔️', color: '#ef4444', glow: 'rgba(239,68,68,0.25)',  bg: 'rgba(239,68,68,0.06)',  multi: '1.5x', rate: '15 OWL/hr',  prob: '30%', desc: 'Guardian class. Protects territory, elevated earnings.' },
  3: { name: 'Overseer', icon: '👁️', color: '#f97316', glow: 'rgba(249,115,22,0.25)',  bg: 'rgba(249,115,22,0.06)',  multi: '2.0x', rate: '20 OWL/hr',  prob: '15%', desc: 'Rare commander. Coordinates agents, doubled yield.' },
  4: { name: 'Elder',    icon: '🔮', color: '#b91c1c', glow: 'rgba(185,28,28,0.35)',  bg: 'rgba(185,28,28,0.06)',  multi: '3.0x', rate: '30 OWL/hr',  prob: '5%',  desc: 'Mythic leader. Triple earnings. Almost never drops.' },
};

const PRICE_THRESHOLDS = [
  { from: 1,      to: 1_000,   price: 0.0001, label: '0.0001 ETH' },
  { from: 1_001,  to: 10_000,  price: 0.001,  label: '0.001 ETH'  },
  { from: 10_001, to: 25_000,  price: 0.01,   label: '0.01 ETH'   },
];

// ─── Main page ────────────────────────────────────────────────────────────────
export default function MintAgent() {
  const [wallet, setWallet]           = useState(null);
  const [quantity, setQuantity]       = useState(1);
  const [mintState, setMintState]     = useState('idle'); // idle | minting | revealing | done | error
  const [mintedTokens, setMintedTokens] = useState([]); // [{tokenId, tier, revealed}]
  const [revealIdx, setRevealIdx]     = useState(0);
  const [error, setError]             = useState(null);
  const [chainData, setChainData]     = useState({
    totalMinted: 0, remaining: 25000, currentPrice: 0.0001,
    batchPrice: 0.0001, mintOpen: true, nextThreshold: 1000, nextPrice: 0.001,
  });
  const [txHash, setTxHash]           = useState(null);
  const pollRef = useRef(null);

  // ── Fetch chain state ──
  const fetchChainData = useCallback(async (qty = quantity) => {
    try {
      const provider = getReadProvider();
      const contract = new ethers.Contract(MINT_CONTRACT_ADDRESS, MINT_ABI, provider);
      const [
        genesisSupply, nextAgentId, currentPrice,
        mintOpen, maxGenesis,
      ] = await Promise.all([
        contract.genesisSupply(),
        contract.nextAgentId(),
        contract.currentPrice(),
        contract.mintOpen(),
        contract.MAX_GENESIS(),
      ]);
      setChainData({
        totalMinted:    Number(genesisSupply),
        remaining:      Number(maxGenesis) - Number(genesisSupply),
        currentPrice:   parseFloat(ethers.formatEther(currentPrice)),
        batchPrice:     parseFloat(ethers.formatEther(currentPrice)) * qty,
        mintOpen,
        nextThreshold:  Number(genesisSupply) + 1,
        nextPrice:      parseFloat(ethers.formatEther(currentPrice)),
      });
    } catch (e) {
      console.warn('Chain data fetch failed:', e.message);
    }
  }, [quantity]);

  useEffect(() => { fetchChainData(); }, [fetchChainData]);
  useEffect(() => { fetchChainData(quantity); }, [quantity, fetchChainData]);

  // ── Wallet ──
  const connectWallet = async () => {
    if (!window.ethereum) { setError('Install MetaMask to mint'); return; }
    try {
      await enforceBaseNetwork();
      const provider = new ethers.BrowserProvider(window.ethereum);
      const accounts = await provider.send('eth_requestAccounts', []);
      setWallet(accounts[0]);
      setError(null);
    } catch (e) { setError(e.message); }
  };

  useEffect(() => {
    if (!window.ethereum) return;
    window.ethereum.on('accountsChanged', ([a]) => setWallet(a || null));
    return () => window.ethereum.removeListener('accountsChanged', () => {});
  }, []);

  // ── Mint ──
  const handleMint = async () => {
    if (!wallet) { connectWallet(); return; }
    setError(null);
    setMintState('minting');
    setMintedTokens([]);

    try {
      await enforceBaseNetwork();
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer   = await provider.getSigner();
      const contract = new ethers.Contract(MINT_CONTRACT_ADDRESS, MINT_ABI, signer);

      // Get exact cost from contract
      const cost = await contract.currentPrice();
      const totalCost = BigInt(cost) * BigInt(quantity);
      
      console.log('🦉 Minting quantity:', quantity);
      console.log('🦉 Current price:', ethers.formatEther(cost), 'ETH');
      console.log('🦉 Total cost:', ethers.formatEther(totalCost), 'ETH');
      console.log('🦉 Contract address:', MINT_CONTRACT_ADDRESS);
      console.log('🦉 Signer address:', await signer.getAddress());
      
      // Estimate gas first
      try {
        const gasEstimate = await contract.mint.estimateGas(quantity, { value: totalCost });
        console.log('🦉 Gas estimate:', gasEstimate.toString());
      } catch (estimateError) {
        console.error('🦉 Gas estimate failed:', estimateError);
        setError('Cannot estimate gas for mint: ' + estimateError.message);
        setMintState('error');
        return;
      }
      
      const tx   = await contract.mint(quantity, { 
        value: totalCost,
        gasLimit: 500000 // Even higher gas limit
      });
      setTxHash(tx.hash);

      // Watch for GenesisMinted events
      const receipt = await tx.wait();
      const iface   = new ethers.Interface(MINT_ABI);
      const tokens  = receipt.logs
        .map(log => { try { return iface.parseLog(log); } catch { return null; } })
        .filter(e => e?.name === 'GenesisMinted')
        .map(e => ({
          tokenId:    e.args.agentId.toString(),
          tier:       Number(e.args.tier),
          multiplier: Number(e.args.multiplier),
          pricePaid:  ethers.formatEther(totalCost) / quantity,
          revealed:   true, // HootlandAgent reveals tier immediately
          x:          Number(e.args.x),
          y:          Number(e.args.y),
        }));

      setMintedTokens(tokens);
      setRevealIdx(0);
      setMintState('done'); // Skip reveal step since tier is revealed immediately
      fetchChainData();
    } catch (e) {
      setError(e.reason || e.message || 'Mint failed');
      setMintState('error');
    }
  };

  // ── Reveal one token ──
  const handleReveal = async (token, idx) => {
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer   = await provider.getSigner();
      const contract = new ethers.Contract(MINT_CONTRACT_ADDRESS, MINT_ABI, signer);
      await (await contract.reveal(token.tokenId)).wait();
      setMintedTokens(prev =>
        prev.map((t, i) => i === idx ? { ...t, revealed: true } : t)
      );
      if (idx + 1 >= mintedTokens.length) setMintState('done');
    } catch (e) {
      setError(e.reason || e.message || 'Reveal failed');
    }
  };

  const mintAgain = () => {
    setMintState('idle');
    setMintedTokens([]);
    setTxHash(null);
    setError(null);
    setQuantity(1);
    fetchChainData(1);
  };

  // ── Progress bar ──
  const pct = (chainData.totalMinted / 100000) * 100;
  const currentThresholdPct = (() => {
    const t = PRICE_THRESHOLDS.find(t => chainData.totalMinted < t.to) || PRICE_THRESHOLDS[3];
    const range = t.to - t.from + 1;
    const progress = chainData.totalMinted - t.from + 1;
    return Math.max(0, Math.min(100, (progress / range) * 100));
  })();

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: globalStyles }} />
      <div style={css.root}>
      {/* Scanline overlay */}
      <div style={css.scanlines} />
      {/* Noise grain */}
      <div style={css.grain} />
      {/* Radial glow */}
      <div style={css.radialGlow} />

      {/* ── Header ── */}
      <header style={css.header}>
        <a href="/" style={css.backLink}>← Hootland</a>
        <div style={css.headerTitle}>
          <span style={css.owlGlyph}>🦉</span>
          <span>AGENT MINT</span>
        </div>
        {wallet ? (
          <div style={css.walletPill}>
            <span style={css.walletDot} />
            {wallet.slice(0,6)}…{wallet.slice(-4)}
          </div>
        ) : (
          <button style={css.connectBtn} onClick={connectWallet}>Connect</button>
        )}
      </header>

      <main style={css.main}>

        {/* ── Hero ── */}
        <section style={css.hero}>
          <div style={css.heroEyebrow}>100,000 Agents · Base Mainnet · Mint-to-Reveal</div>
          <h1 style={css.heroTitle}>
            <span style={css.heroLine1}>SUMMON YOUR</span>
            <span style={css.heroLine2}>AGENT</span>
          </h1>
          <p style={css.heroSub}>
            Each mint reveals a random tier. Earn owlSDT passively.<br />
            Price rises as agents are claimed. Mint early, mint cheap.
          </p>
        </section>

        {/* ── Supply & Price Status ── */}
        <section style={css.statusBar}>
          <div style={css.statusItem}>
            <div style={css.statusVal}>{chainData.totalMinted.toLocaleString()}</div>
            <div style={css.statusLabel}>Minted</div>
          </div>
          <div style={css.statusDivider} />
          <div style={css.statusItem}>
            <div style={css.statusVal}>{chainData.remaining.toLocaleString()}</div>
            <div style={css.statusLabel}>Remaining</div>
          </div>
          <div style={css.statusDivider} />
          <div style={css.statusItem}>
            <div style={{ ...css.statusVal, color: '#fbbf24' }}>
              {chainData.currentPrice} ETH
            </div>
            <div style={css.statusLabel}>Current Price</div>
          </div>
          <div style={css.statusDivider} />
          <div style={css.statusItem}>
            <div style={css.statusVal}>{chainData.nextThreshold.toLocaleString()}</div>
            <div style={css.statusLabel}>Next Price Rise at</div>
          </div>
        </section>

        {/* ── Supply bar ── */}
        <div style={css.supplyBarWrap}>
          <div style={css.supplyBarTrack}>
            <div style={{ ...css.supplyBarFill, width: `${pct}%` }} />
          </div>
          <div style={css.supplyBarLabels}>
            <span>0</span>
            <span style={{ color: '#fbbf24', fontSize: 11 }}>
              ↑ next price at {chainData.nextThreshold.toLocaleString()} ({chainData.nextPrice} ETH)
            </span>
            <span>25,000</span>
          </div>
        </div>

        {/* ── Price ladder ── */}
        <section style={css.priceLadder}>
          <div style={css.ladderTitle}>PRICE LADDER</div>
          <div style={css.ladderGrid}>
            {PRICE_THRESHOLDS.map((t, i) => {
              const active = chainData.totalMinted >= t.from && chainData.totalMinted <= t.to;
              const done   = chainData.totalMinted > t.to;
              return (
                <div key={i} style={{
                  ...css.ladderItem,
                  ...(active ? css.ladderItemActive : {}),
                  ...(done   ? css.ladderItemDone   : {}),
                }}>
                  <div style={css.ladderRange}>
                    {t.from.toLocaleString()} – {t.to.toLocaleString()}
                  </div>
                  <div style={css.ladderPrice}>{t.label}</div>
                  {active && <div style={css.ladderActiveDot}>● NOW</div>}
                  {done   && <div style={css.ladderDone}>✓ DONE</div>}
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Tier odds ── */}
        <section style={css.tierSection}>
          <div style={css.tierTitle}>TIER DROP RATES</div>
          <div style={css.tierGrid}>
            {Object.entries(TIERS).map(([id, tier]) => (
              <div key={id} style={{
                ...css.tierCard,
                borderColor: tier.color + '44',
                background: tier.bg,
              }}>
                <div style={{ ...css.tierIcon, filter: `drop-shadow(0 0 8px ${tier.color})` }}>
                  {tier.icon}
                </div>
                <div style={{ ...css.tierName, color: tier.color }}>{tier.name}</div>
                <div style={{ ...css.tierProb, color: tier.color }}>{tier.prob}</div>
                <div style={css.tierMeta}>
                  <span>{tier.multi} multiplier</span>
                  <span>{tier.rate}</span>
                </div>
                <div style={css.tierDesc}>{tier.desc}</div>
                {/* Probability bar */}
                <div style={css.probBarBg}>
                  <div style={{
                    ...css.probBarFill,
                    width: tier.prob,
                    background: tier.color,
                    boxShadow: `0 0 8px ${tier.color}`,
                  }} />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Mint panel ── */}
        {mintState === 'idle' || mintState === 'error' ? (
          <section style={css.mintPanel}>
            <div style={css.mintPanelInner}>

              {/* Mystery box visual */}
              <div style={css.mysteryBox}>
                <div style={css.mysteryGlow} />
                <div style={css.mysteryOwl}>🦉</div>
                <div style={css.mysteryQuestion}>?</div>
                <div style={css.mysteryLabel}>UNKNOWN TIER</div>
              </div>

              {/* Quantity selector */}
              <div style={css.qtySection}>
                <div style={css.qtyLabel}>QUANTITY (MAX 10)</div>
                <div style={css.qtyRow}>
                  <button style={css.qtyBtn}
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}>−</button>
                  <div style={css.qtyDisplay}>{quantity}</div>
                  <button style={{ ...css.qtyBtn, borderRadius: '0 8px 8px 0' }}
                    onClick={() => setQuantity(q => Math.min(10, q + 1))}>+</button>
                </div>
                {/* Quick selects */}
                <div style={css.qtyQuick}>
                  {[1,3,5,10].map(n => (
                    <button key={n}
                      style={{ ...css.qtyQuickBtn, ...(quantity === n ? css.qtyQuickActive : {}) }}
                      onClick={() => setQuantity(n)}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              {/* Cost summary */}
              <div style={css.costSummary}>
                <div style={css.costRow}>
                  <span>Price per agent</span>
                  <span>{chainData.currentPrice} ETH</span>
                </div>
                <div style={css.costRow}>
                  <span>Quantity</span>
                  <span>× {quantity}</span>
                </div>
                <div style={css.costDivider} />
                <div style={{ ...css.costRow, ...css.costTotal }}>
                  <span>Total</span>
                  <span style={{ color: '#fbbf24' }}>{chainData.batchPrice.toFixed(4)} ETH</span>
                </div>
              </div>

              {error && (
                <div style={css.errorBox}>⚠ {error}</div>
              )}

              {!chainData.mintOpen && (
                <div style={css.closedBox}>Mint is currently closed</div>
              )}

              {/* Mint button */}
              <button
                style={{
                  ...css.mintBtn,
                  ...(!chainData.mintOpen ? css.mintBtnDisabled : {}),
                }}
                onClick={handleMint}
                disabled={!chainData.mintOpen}
              >
                {!wallet
                  ? 'CONNECT WALLET TO MINT'
                  : `MINT ${quantity} AGENT${quantity > 1 ? 'S' : ''} · ${chainData.batchPrice.toFixed(4)} ETH` 
                }
              </button>

              <div style={css.mintNote}>
                Tier is revealed randomly on-chain. Each mint is final.<br />
                Excess ETH is automatically refunded.
              </div>
            </div>
          </section>
        ) : mintState === 'minting' ? (
          <MintingState txHash={txHash} />
        ) : mintState === 'revealing' || mintState === 'done' ? (
          <RevealState
            tokens={mintedTokens}
            onReveal={handleReveal}
            onDone={mintAgain}
            txHash={txHash}
            isDone={mintState === 'done'}
          />
        ) : null}

      </main>
    </div>
    </>
  );
}

// ─── Minting in progress ──────────────────────────────────────────────────────

function MintingState({ txHash }) {
  return (
    <section style={css.mintPanel}>
      <div style={{ ...css.mintPanelInner, textAlign: 'center', padding: '48px 24px' }}>
        <div style={css.mintingOwl}>🦉</div>
        <div style={css.mintingTitle}>SUMMONING AGENTS…</div>
        <div style={css.mintingSub}>Confirm in MetaMask. Do not close this page.</div>
        {txHash && (
          <a
            href={`https://basescan.org/tx/${txHash}`}
            target="_blank" rel="noreferrer"
            style={css.txLink}
          >
            View transaction on Basescan →
          </a>
        )}
      </div>
    </section>
  );
}

// ─── Reveal cards ─────────────────────────────────────────────────────────────

function RevealState({ tokens, onReveal, onDone, txHash, isDone }) {
  return (
    <section style={css.mintPanel}>
      <div style={css.revealHeader}>
        <div style={css.revealTitle}>
          {isDone ? '🎉 ALL REVEALED' : '✨ REVEAL YOUR AGENTS'}
        </div>
        <div style={css.revealSub}>
          {isDone
            ? 'Deploy them in Hootland to start earning owlSDT'
            : 'Click each card to reveal your tier'}
        </div>
      </div>

      <div style={css.revealGrid}>
        {tokens.map((token, idx) => (
          <RevealCard
            key={token.tokenId}
            token={token}
            idx={idx}
            onReveal={() => onReveal(token, idx)}
          />
        ))}
      </div>

      {txHash && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <a href={`https://basescan.org/tx/${txHash}`}
            target="_blank" rel="noreferrer" style={css.txLink}>
            View mint tx →
          </a>
        </div>
      )}

      {isDone && (
        <div style={css.doneActions}>
          <a href="/world" style={css.deployBtn}>Enter Hootland World →</a>
          <button style={css.mintAgainBtn} onClick={onDone}>Mint More</button>
        </div>
      )}
    </section>
  );
}

function RevealCard({ token, onReveal }) {
  const [isRevealing, setIsRevealing] = useState(false);
  const tier = TIERS[token.tier] || TIERS[1];

  const handleClick = async () => {
    if (token.revealed || isRevealing) return;
    setIsRevealing(true);
    await onReveal();
    setIsRevealing(false);
  };

  return (
    <div
      style={{
        ...css.revealCard,
        ...(token.revealed ? {
          borderColor: tier.color + '88',
          background: tier.bg,
          boxShadow: `0 0 32px ${tier.glow}`,
        } : {}),
        cursor: token.revealed ? 'default' : 'pointer',
      }}
      onClick={handleClick}
    >
      {token.revealed ? (
        <>
          <div style={{ ...css.revealCardIcon, filter: `drop-shadow(0 0 12px ${tier.color})` }}>
            {tier.icon}
          </div>
          <div style={{ ...css.revealCardTier, color: tier.color }}>{tier.name}</div>
          <div style={css.revealCardId}>#{token.tokenId}</div>
          <div style={css.revealCardMeta}>
            <span style={{ color: tier.color }}>{tier.multi}</span>
            <span>{tier.rate}</span>
          </div>
          <div style={{ ...css.revealCardProb, color: tier.color }}>
            {tier.prob} drop rate
          </div>
        </>
      ) : (
        <>
          <div style={css.revealCardMystery}>
            {isRevealing ? '⏳' : '🎁'}
          </div>
          <div style={css.revealCardId}>#{token.tokenId}</div>
          <div style={css.revealCardHint}>
            {isRevealing ? 'Revealing…' : 'Tap to reveal'}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const css = {
  root: {
    minHeight: '100vh',
    background: '#0a0a0f',
    color: '#e2e8f0',
    fontFamily: '"Share Tech Mono", "IBM Plex Mono", "Courier New", monospace',
    position: 'relative',
    overflowX: 'hidden',
    overflowY: 'auto',
  },
  scanlines: {
    position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none',
    background: `repeating-linear-gradient(
      0deg,
      transparent,
      transparent 2px,
      rgba(220,38,38,0.07) 2px,
      rgba(220,38,38,0.07) 4px
    )`,
  },
  grain: {
    position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none', opacity: 0.04,
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'repeat',
  },
  radialGlow: {
    position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none',
    background: 'radial-gradient(ellipse 80% 50% at 50% -10%, rgba(220,38,38,0.12), transparent)',
  },

  // Header
  header: {
    position: 'relative', zIndex: 10,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 28px',
    borderBottom: '1px solid rgba(220,38,38,0.15)',
    background: 'rgba(10,10,15,0.85)', backdropFilter: 'blur(12px)',
  },
  backLink: {
    color: '#dc2626', textDecoration: 'none', fontSize: 13,
    letterSpacing: '0.05em',
  },
  headerTitle: {
    display: 'flex', alignItems: 'center', gap: 10,
    fontSize: 16, fontWeight: 700, letterSpacing: '0.2em', color: '#dc2626',
  },
  owlGlyph: { fontSize: 22 },
  walletPill: {
    display: 'flex', alignItems: 'center', gap: 7,
    fontSize: 12, color: '#dc2626',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 20, padding: '5px 12px',
  },
  walletDot: {
    width: 6, height: 6, borderRadius: '50%', background: '#dc2626',
    boxShadow: '0 0 6px #dc2626',
  },
  connectBtn: {
    background: '#dc2626',
    border: '1px solid rgba(220,38,38,0.4)',
    borderRadius: 8, padding: '7px 18px',
    color: '#0a0a0f', fontSize: 13, cursor: 'pointer',
    fontFamily: 'inherit', letterSpacing: '0.1em',
  },

  // Main
  main: {
    position: 'relative', zIndex: 2,
    maxWidth: 900, margin: '0 auto', padding: '48px 24px 80px',
  },

  // Hero
  hero: { textAlign: 'center', marginBottom: 48 },
  heroEyebrow: {
    fontSize: 11, letterSpacing: '0.3em', color: '#6b6b8a',
    marginBottom: 20, textTransform: 'uppercase',
  },
  heroTitle: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    margin: '0 0 20px', lineHeight: 1,
  },
  heroLine1: {
    fontSize: 'clamp(28px, 6vw, 52px)',
    fontWeight: 400, letterSpacing: '0.25em', color: '#6b6b8a',
  },
  heroLine2: {
    fontSize: 'clamp(56px, 14vw, 120px)',
    fontWeight: 700, letterSpacing: '0.1em',
    background: 'linear-gradient(135deg, #a78bfa 0%, #fbbf24 60%, #f97316 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    lineHeight: 1.05,
  },
  heroSub: {
    fontSize: 14, color: '#6b6b8a', lineHeight: 1.8,
    maxWidth: 480, margin: '0 auto',
  },

  // Status bar
  statusBar: {
    display: 'flex', alignItems: 'center',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(167,139,250,0.15)',
    borderRadius: 12, padding: '20px 24px',
    marginBottom: 16,
  },
  statusItem: { flex: 1, textAlign: 'center' },
  statusVal: { fontSize: 22, fontWeight: 700, color: '#e2e2f0', marginBottom: 4 },
  statusLabel: { fontSize: 10, letterSpacing: '0.15em', color: '#6b6b8a', textTransform: 'uppercase' },
  statusDivider: { width: 1, height: 40, background: 'rgba(167,139,250,0.15)', margin: '0 12px' },

  // Supply bar
  supplyBarWrap: { marginBottom: 40 },
  supplyBarTrack: {
    height: 6, background: 'rgba(255,255,255,0.06)',
    borderRadius: 3, overflow: 'hidden', marginBottom: 8,
  },
  supplyBarFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #4ade80, #a78bfa, #fbbf24)',
    borderRadius: 3, transition: 'width 1s',
  },
  supplyBarLabels: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: 10, color: '#6b6b8a',
  },

  // Price ladder
  priceLadder: { marginBottom: 48 },
  ladderTitle: {
    fontSize: 11, letterSpacing: '0.25em', color: '#6b6b8a',
    marginBottom: 16, textTransform: 'uppercase',
  },
  ladderGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10,
  },
  ladderItem: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 8, padding: '12px 14px',
    position: 'relative',
  },
  ladderItemActive: {
    background: 'rgba(251,191,36,0.06)',
    border: '1px solid rgba(251,191,36,0.35)',
    boxShadow: '0 0 16px rgba(251,191,36,0.1)',
  },
  ladderItemDone: { opacity: 0.35 },
  ladderRange: { fontSize: 10, color: '#6b6b8a', marginBottom: 6 },
  ladderPrice: { fontSize: 15, fontWeight: 700, color: '#d4d4e8' },
  ladderActiveDot: {
    marginTop: 6, fontSize: 10, color: '#fbbf24',
    letterSpacing: '0.1em',
  },
  ladderDone: {
    marginTop: 6, fontSize: 10, color: '#4ade80',
    letterSpacing: '0.1em',
  },

  // Tier odds
  tierSection: { marginBottom: 48 },
  tierTitle: {
    fontSize: 11, letterSpacing: '0.25em', color: '#6b6b8a',
    marginBottom: 16, textTransform: 'uppercase',
  },
  tierGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12,
  },
  tierCard: {
    border: '1px solid', borderRadius: 12, padding: '20px 16px',
    textAlign: 'center',
  },
  tierIcon: { fontSize: 32, marginBottom: 10, display: 'block' },
  tierName: { fontSize: 14, fontWeight: 700, letterSpacing: '0.1em', marginBottom: 4 },
  tierProb: { fontSize: 24, fontWeight: 700, marginBottom: 8 },
  tierMeta: {
    display: 'flex', flexDirection: 'column', gap: 2,
    fontSize: 10, color: '#6b6b8a', marginBottom: 8,
  },
  tierDesc: { fontSize: 11, color: '#6b6b8a', lineHeight: 1.5, marginBottom: 12 },
  probBarBg: {
    height: 3, background: 'rgba(255,255,255,0.06)',
    borderRadius: 2, overflow: 'hidden',
  },
  probBarFill: { height: '100%', borderRadius: 2, transition: 'width 1s' },

  // Mint panel
  mintPanel: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(167,139,250,0.2)',
    borderRadius: 16, overflow: 'hidden',
  },
  mintPanelInner: { padding: '36px 40px' },

  // Mystery box
  mysteryBox: {
    position: 'relative', textAlign: 'center',
    marginBottom: 32,
    padding: '28px 0',
    background: 'rgba(167,139,250,0.04)',
    border: '1px solid rgba(167,139,250,0.12)',
    borderRadius: 12,
  },
  mysteryGlow: {
    position: 'absolute', inset: 0,
    background: 'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(167,139,250,0.08), transparent)',
    pointerEvents: 'none',
  },
  mysteryOwl: {
    fontSize: 64, display: 'block',
    animation: 'float 3s ease-in-out infinite',
  },
  mysteryQuestion: {
    fontSize: 28, fontWeight: 700, color: '#6b6b8a',
    letterSpacing: '0.2em', marginTop: 4,
  },
  mysteryLabel: {
    fontSize: 10, letterSpacing: '0.3em', color: '#6b6b8a',
    marginTop: 8, textTransform: 'uppercase',
  },

  // Qty
  qtySection: { marginBottom: 24, textAlign: 'center' },
  qtyLabel: {
    fontSize: 10, letterSpacing: '0.2em', color: '#6b6b8a',
    marginBottom: 12, textTransform: 'uppercase',
  },
  qtyRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0,
    marginBottom: 12,
  },
  qtyBtn: {
    width: 44, height: 44,
    background: 'rgba(167,139,250,0.1)',
    border: '1px solid rgba(167,139,250,0.25)',
    color: '#a78bfa', fontSize: 20, cursor: 'pointer',
    fontFamily: 'inherit',
    borderRadius: '8px 0 0 8px',
  },
  qtyDisplay: {
    width: 72, height: 44,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(167,139,250,0.06)',
    border: '1px solid rgba(167,139,250,0.25)',
    borderLeft: 'none', borderRight: 'none',
    fontSize: 22, fontWeight: 700, color: '#e2e2f0',
  },
  qtyQuick: {
    display: 'flex', justifyContent: 'center', gap: 8,
  },
  qtyQuickBtn: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 6, padding: '4px 14px',
    color: '#6b6b8a', fontSize: 13, cursor: 'pointer',
    fontFamily: 'inherit',
  },
  qtyQuickActive: {
    background: 'rgba(167,139,250,0.15)',
    borderColor: 'rgba(167,139,250,0.4)',
    color: '#a78bfa',
  },

  // Cost
  costSummary: {
    background: 'rgba(0,0,0,0.3)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 10, padding: '16px 20px',
    marginBottom: 20,
  },
  costRow: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: 13, color: '#6b6b8a', marginBottom: 8,
  },
  costDivider: {
    height: 1, background: 'rgba(255,255,255,0.06)',
    margin: '8px 0 12px',
  },
  costTotal: { fontSize: 15, fontWeight: 700, color: '#e2e2f0', marginBottom: 0 },

  // Errors
  errorBox: {
    background: 'rgba(248,113,113,0.08)',
    border: '1px solid rgba(248,113,113,0.25)',
    borderRadius: 8, padding: '12px 16px',
    fontSize: 12, color: '#f87171', marginBottom: 16,
  },
  closedBox: {
    background: 'rgba(251,191,36,0.06)',
    border: '1px solid rgba(251,191,36,0.25)',
    borderRadius: 8, padding: '12px 16px',
    fontSize: 12, color: '#fbbf24',
    textAlign: 'center', marginBottom: 16,
  },

  // Mint button
  mintBtn: {
    width: '100%',
    background: 'linear-gradient(135deg, #7c3aed, #a78bfa)',
    border: 'none', borderRadius: 10, padding: '18px',
    fontSize: 14, fontWeight: 700, letterSpacing: '0.12em',
    color: '#fff', cursor: 'pointer', fontFamily: 'inherit',
    boxShadow: '0 0 32px rgba(167,139,250,0.25)',
    transition: 'all 0.2s',
    marginBottom: 16,
    textTransform: 'uppercase',
  },
  mintBtnDisabled: {
    opacity: 0.4, cursor: 'not-allowed',
    background: 'rgba(255,255,255,0.06)',
    boxShadow: 'none',
  },
  mintNote: {
    textAlign: 'center', fontSize: 11, color: '#6b6b8a', lineHeight: 1.6,
  },

  // Minting state
  mintingOwl: {
    fontSize: 64, marginBottom: 20,
    display: 'block',
    animation: 'pulse 1.2s ease-in-out infinite',
  },
  mintingTitle: {
    fontSize: 18, fontWeight: 700, letterSpacing: '0.2em',
    color: '#a78bfa', marginBottom: 12,
  },
  mintingSub: { fontSize: 13, color: '#6b6b8a', marginBottom: 20 },

  // Reveal
  revealHeader: { padding: '32px 32px 0', textAlign: 'center', marginBottom: 24 },
  revealTitle: { fontSize: 20, fontWeight: 700, letterSpacing: '0.15em', color: '#e2e2f0', marginBottom: 8 },
  revealSub: { fontSize: 13, color: '#6b6b8a' },
  revealGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: 12, padding: '0 24px 24px',
  },
  revealCard: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 12, padding: '20px 14px',
    textAlign: 'center', transition: 'all 0.4s',
    minHeight: 160,
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
  },
  revealCardIcon: { fontSize: 36, marginBottom: 10, display: 'block' },
  revealCardTier: { fontSize: 13, fontWeight: 700, letterSpacing: '0.1em', marginBottom: 4 },
  revealCardId: { fontSize: 11, color: '#6b6b8a', marginBottom: 6 },
  revealCardMeta: {
    display: 'flex', flexDirection: 'column', gap: 2,
    fontSize: 10, color: '#6b6b8a',
  },
  revealCardProb: { fontSize: 10, marginTop: 6 },
  revealCardMystery: { fontSize: 40, marginBottom: 10 },
  revealCardHint: {
    fontSize: 11, color: '#6b6b8a', letterSpacing: '0.1em',
    textTransform: 'uppercase',
  },

  // Done actions
  doneActions: {
    display: 'flex', gap: 12, justifyContent: 'center',
    padding: '0 24px 32px',
  },
  deployBtn: {
    background: 'linear-gradient(135deg, #7c3aed, #a78bfa)',
    border: 'none', borderRadius: 8, padding: '12px 24px',
    color: '#fff', fontSize: 13, fontWeight: 700,
    cursor: 'pointer', textDecoration: 'none', fontFamily: 'inherit',
    letterSpacing: '0.05em',
  },
  mintAgainBtn: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8, padding: '12px 24px',
    color: '#6b6b8a', fontSize: 13, cursor: 'pointer',
    fontFamily: 'inherit',
  },

  txLink: {
    display: 'inline-block', fontSize: 12, color: '#a78bfa',
    textDecoration: 'none', letterSpacing: '0.05em',
  },
};
